/**
 * SalesConsole — staff sales tools that update a customer's trial/plan/limits in
 * Mongo, keyed by Stripe customer ID (cus_…, looked up via payment.stripe_id).
 *
 * Flow per card: enter Stripe ID + the action field → "Look up" (read-only
 * preview of the affected customer + current→new) → "Apply" to commit. The
 * preview guards against applying to the wrong customer.
 */
import { useState } from 'react';
import { Button, Input, Label } from '@staff-app/ui';
import {
  useSalesLookup,
  useSalesExtendTrial,
  useSalesResetToFreeTrial,
  useSalesUpliftToEngage,
  useSalesSetScreensLimit,
  type SalesActionResult,
  type SalesLookupResult,
} from '../hooks/useSalesConsole';

type FieldType = 'date' | 'number';

/** Apply the action keyed by `config.key`, returning the affected customer. */
type ApplyFn = (stripeId: string, value: string) => Promise<SalesActionResult>;

interface ActionConfig {
  key: string;
  title: string;
  description: string;
  fieldLabel: string;
  fieldType: FieldType;
  fieldPlaceholder?: string;
  buttonLabel: string;
}

const ACTIONS: ActionConfig[] = [
  {
    key: 'extend-trial',
    title: 'Extend Customer Trial',
    description: "Set a new trial end date for the customer's existing trial.",
    fieldLabel: 'Trial end date',
    fieldType: 'date',
    buttonLabel: 'Extend trial',
  },
  {
    key: 'reset-free-trial',
    title: 'Reset Free Plan to Free Trial',
    description: 'Move a free-plan customer onto a free trial ending on the chosen date.',
    fieldLabel: 'Trial end date',
    fieldType: 'date',
    buttonLabel: 'Reset to free trial',
  },
  {
    key: 'uplift-proplus-engage',
    title: 'Uplift Pro Plus to Engage (Free Trial)',
    description: 'Uplift a Pro Plus customer to the Engage plan as a free trial ending on the chosen date.',
    fieldLabel: 'Trial end date',
    fieldType: 'date',
    buttonLabel: 'Uplift to Engage',
  },
  {
    key: 'increase-screens',
    title: 'Increase Trial Screens Limit',
    description: 'Set the total number of screens the trial customer is allowed.',
    fieldLabel: 'Total screens limit',
    fieldType: 'number',
    fieldPlaceholder: 'e.g. 25',
    buttonLabel: 'Update screens limit',
  },
];

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
}

/** The "current → new" line shown in the preview, per action type. */
function diffLine(config: ActionConfig, preview: SalesLookupResult, value: string): string {
  if (config.fieldType === 'number') {
    return `Screens limit: ${preview.maxQuantity ?? '—'} → ${value}`;
  }
  return `Trial end: ${fmtDate(preview.trialEndDate)} → ${value}`;
}

function SalesActionCard({ config, onApply }: { config: ActionConfig; onApply: ApplyFn }) {
  const [stripeId, setStripeId] = useState('');
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState<SalesLookupResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [runLookup] = useSalesLookup();

  const valid = stripeId.trim().startsWith('cus_') && value.trim().length > 0;

  const reset = () => {
    setPreview(null);
    setStripeId('');
    setValue('');
  };

  const handleLookup = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await runLookup({ variables: { stripeId: stripeId.trim() } });
      if (res.error) throw res.error;
      if (!res.data?.staffSalesLookup) throw new Error('Customer not found');
      setPreview(res.data.staffSalesLookup);
    } catch (e: unknown) {
      setMessage({ kind: 'err', text: (e as Error)?.message ?? 'Lookup failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await onApply(stripeId.trim(), value.trim());
      const who = result.companyName ? `${result.companyName} — ` : '';
      setMessage({ kind: 'ok', text: `${who}${result.message}` });
      reset();
    } catch (e: unknown) {
      setMessage({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid={`sales-card-${config.key}`}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{config.title}</h3>
        <p className="text-xs text-muted-foreground">{config.description}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${config.key}-stripe`} className="text-xs">
          {'Stripe customer ID'}
        </Label>
        <Input
          id={`${config.key}-stripe`}
          value={stripeId}
          onChange={(e) => {
            setStripeId(e.target.value);
            setPreview(null);
          }}
          placeholder="cus_..."
          data-testid={`sales-${config.key}-stripe`}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${config.key}-value`} className="text-xs">
          {config.fieldLabel}
        </Label>
        <Input
          id={`${config.key}-value`}
          type={config.fieldType}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setPreview(null);
          }}
          placeholder={config.fieldPlaceholder}
          min={config.fieldType === 'number' ? 1 : undefined}
          data-testid={`sales-${config.key}-value`}
        />
      </div>

      {preview ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-dashed bg-muted/30 p-3"
          data-testid={`sales-${config.key}-preview`}
        >
          <div className="text-xs text-foreground">
            <span className="font-semibold">{preview.companyName ?? preview.accountId}</span>
            {preview.planFeature ? (
              <span className="text-muted-foreground">
                {' · '}
                {preview.plan ?? '—'}/{preview.planFeature}
                {preview.isTrial ? ' · trial' : ''}
              </span>
            ) : null}
          </div>
          <div className="text-xs font-medium text-foreground">{diffLine(config, preview, value.trim())}</div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleApply}
              disabled={busy}
              size="sm"
              className="cursor-pointer"
              data-testid={`sales-${config.key}-apply`}
            >
              {busy ? 'Applying...' : `Confirm: ${config.buttonLabel}`}
            </Button>
            <Button
              onClick={() => setPreview(null)}
              disabled={busy}
              size="sm"
              variant="ghost"
              className="cursor-pointer"
              data-testid={`sales-${config.key}-cancel`}
            >
              {'Cancel'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={handleLookup}
          disabled={!valid || busy}
          className="w-fit cursor-pointer"
          variant="outline"
          data-testid={`sales-${config.key}-lookup`}
        >
          {busy ? 'Looking up...' : 'Look up customer'}
        </Button>
      )}

      {message ? (
        <p
          className={message.kind === 'ok' ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}
          data-testid={`sales-${config.key}-result`}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

export function SalesConsole() {
  const [extendTrial] = useSalesExtendTrial();
  const [resetToFreeTrial] = useSalesResetToFreeTrial();
  const [upliftToEngage] = useSalesUpliftToEngage();
  const [setScreensLimit] = useSalesSetScreensLimit();

  // One apply fn per action key. Date actions pass `trialEndDate`; the screens
  // action parses the value to an integer `maxQuantity`.
  const applyByKey: Record<string, ApplyFn> = {
    'extend-trial': async (stripeId, value) =>
      (await extendTrial({ variables: { stripeId, trialEndDate: value } })).data!
        .staffSalesExtendTrial,
    'reset-free-trial': async (stripeId, value) =>
      (await resetToFreeTrial({ variables: { stripeId, trialEndDate: value } })).data!
        .staffSalesResetToFreeTrial,
    'uplift-proplus-engage': async (stripeId, value) =>
      (await upliftToEngage({ variables: { stripeId, trialEndDate: value } })).data!
        .staffSalesUpliftToEngage,
    'increase-screens': async (stripeId, value) =>
      (await setScreensLimit({ variables: { stripeId, maxQuantity: Number(value) } })).data!
        .staffSalesSetScreensLimit,
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="sales-console">
      {ACTIONS.map((action) => (
        <SalesActionCard key={action.key} config={action} onApply={applyByKey[action.key]} />
      ))}
    </div>
  );
}
